import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField, Box, CircularProgress } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';

/**
 * SearchableAreaDropdown
 *
 * Dependent sub-filter for the city-level locality filter.
 * Disabled until a city is selected. Options come from GET /libraries/localities?city=<city>.
 * The "All areas" sentinel is value=''.
 */
function SearchableAreaDropdown({
  value,
  onChange,
  localities,
  loading,
  disabled,
  label,
  placeholder,
  size,
  fullWidth,
  error,
  helperText,
}) {
  const ALL_AREAS_OPTION = { value: '', label: 'All areas', count: null };

  const options = [ALL_AREAS_OPTION, ...localities];

  const selectedOption =
    options.find((opt) => opt.value === (value || '')) || ALL_AREAS_OPTION;

  const handleChange = (_event, newValue) => {
    onChange(newValue ? newValue.value : '');
  };

  return (
    <Autocomplete
      value={selectedOption}
      onChange={handleChange}
      options={options}
      getOptionLabel={(option) => option.label || ''}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      size={size}
      disabled={disabled}
      loading={loading}
      disableClearable={selectedOption.value === ''}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={disabled ? 'Select a city first' : placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {selectedOption.value !== '' && (
                  <PlaceIcon
                    sx={{ color: 'action.active', mr: 1, ml: 0.5 }}
                    fontSize="small"
                  />
                )}
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.value === '' ? '__all__' : option.value}>
          <Box sx={{ flexGrow: 1 }}>{option.label}</Box>
          {option.count !== null && (
            <Box
              component="span"
              sx={{ color: 'text.secondary', fontSize: '0.75rem', ml: 1 }}
            >
              {option.count}
            </Box>
          )}
        </Box>
      )}
      sx={{ width: fullWidth ? '100%' : 250 }}
    />
  );
}

SearchableAreaDropdown.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  localities: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
    })
  ),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  fullWidth: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
};

SearchableAreaDropdown.defaultProps = {
  value: '',
  localities: [],
  loading: false,
  disabled: false,
  label: 'Area',
  placeholder: 'Search area...',
  size: 'medium',
  fullWidth: false,
  error: false,
  helperText: '',
};

export default SearchableAreaDropdown;
